import { useQuery } from "@noom/hive-core";
import { useCallback, useRef } from "react";

import {
  TreatmentOverviewsDocument,
  TreatmentOverviewsQuery,
} from "gql/graphql";

import { getCurrentLocalDateTime } from "../../../utils/dateUtils";
import { useAuthCredentials } from "../../../hooks/useAuthCredentials";

type TreatmentOverviewResult =
  TreatmentOverviewsQuery["treatmentOverviews"]["treatments"][number];

const SUPPORTED_TREATMENT_TYPENAMES = [
  "RxTreatmentOverview",
  "OTCTreatmentOverview",
  "LabTreatmentOverview",
] as const;

export type TreatmentOverview = Extract<
  TreatmentOverviewResult,
  { __typename: (typeof SUPPORTED_TREATMENT_TYPENAMES)[number] }
>;

const isSupportedTreatmentOverview = (
  treatment: TreatmentOverviewResult
): treatment is TreatmentOverview =>
  (SUPPORTED_TREATMENT_TYPENAMES as readonly string[]).includes(
    treatment.__typename
  );

export type UseTreatmentOverviewsResult = {
  treatments: TreatmentOverview[];
  headerTitleKey: string | null | undefined;
  hasData: boolean;
  loading: boolean;
  error: Error | undefined;
  refetch: () => void;
};

export const useTreatmentOverviews = (): UseTreatmentOverviewsResult => {
  // Access code comes straight from hive-auth, so `/care` works for any
  // authenticated user — it does not wait on (or require) a patient profile.
  // `treatmentOverviews` is safe to call for everyone: a non-patient simply
  // gets no treatments back, so there's no need to gate on patient status.
  const { accessCode } = useAuthCredentials();
  const initialClientDateTimeRef = useRef<string | undefined>(undefined);
  if (!initialClientDateTimeRef.current) {
    initialClientDateTimeRef.current = getCurrentLocalDateTime();
  }

  const {
    data,
    loading: queryLoading,
    error,
    refetch,
  } = useQuery(TreatmentOverviewsDocument, {
    variables: {
      accessCode: accessCode ?? "",
      clientDateTime: initialClientDateTimeRef.current,
    },
    skip: !accessCode,
  });

  const handleRefetch = useCallback(() => {
    void refetch({ clientDateTime: getCurrentLocalDateTime() });
  }, [refetch]);

  return {
    treatments: (data?.treatmentOverviews.treatments ?? []).filter(
      isSupportedTreatmentOverview
    ),
    headerTitleKey:
      data?.treatmentOverviews.programInfo?.headerTitleKey ?? null,
    hasData: data !== undefined,
    loading: !!accessCode && queryLoading,
    error: data ? undefined : error,
    refetch: handleRefetch,
  };
};
